import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

import { useWindowSize } from 'hooks/useWindowSize';
import { LinkButton } from 'components/Button/Button';
import { Container } from 'components/Layout/Layout';
import Rating from 'components/Rating/Rating';
import { CommunityTags } from 'components/Tags/Tags';
import { ShareOnTwitter } from 'components/Button/ShareButton';
import { Images } from 'assets/images';
import {
  ContentContainer,
  ChallengeDescription,
  ChallengeLabel,
  SectionContainer,
  SectionColumn,
  AuthorContainer,
  AuthorAvatar,
  AuthorName,
  RatingAndTagsContainer,
  RatingContainer,
  RatingLabel,
  MetaInfoContainer,
  SelectedItemImage,
  LinkAndImageContainer,
  RatingAlignRow,
  SponsorLogo,
  ButtonsContainer,
  RatingAndShareContainer,
  Title,
  BigText,
  StarIcon,
  TagsContainer,
} from './styles';

export default function CommunityPageImageAndDescription({ selectedItem, setItemRating, startUrl }) {
  const { width } = useWindowSize();
  const { userRole } = useSelector((state) => state.auth);

  const isMobile = width < 720;

  const [description, setDescription] = useState('');
  const [challengeRating, setChallengeRating] = useState(selectedItem?.currentRating || 0);

  useEffect(() => {
    if (!selectedItem?._id) return;
    setChallengeRating(selectedItem?.currentRating || 0);
    if (selectedItem.landingPageContent) {
      setDescription(selectedItem.landingPageContent.replaceAll('<p>', '<p style="word-break: break-word;">'));
    } else if (selectedItem.description) {
      setDescription(selectedItem.description.replaceAll('<p>', '<p style="word-break: break-word;">'));
    }
  }, [selectedItem]);

  const updateMyRating = (rating) => {
    if (setItemRating) setItemRating(rating);
    setChallengeRating(rating);
  };

  const renderRatingNumberAndTags = () => {
    const ratingNo = selectedItem.averageRating?.toFixed(1);
    return (
      <RatingAndTagsContainer>
        {setItemRating && (
          <RatingContainer>
            <BigText>{ratingNo?.substring(0, 1)}</BigText>
            {ratingNo?.substring(1, ratingNo.length)}
            <StarIcon></StarIcon>
          </RatingContainer>
        )}
        {selectedItem.keywords?.length > 0 && (
          <TagsContainer>
            <CommunityTags size={12} tags={selectedItem.keywords} singleLine />
          </TagsContainer>
        )}
      </RatingAndTagsContainer>
    );
  };

  const renderButtons = () => {
    return (
      <ButtonsContainer>
        {startUrl && (
          <LinkButton wide rounded small removeMargin href={startUrl} icon="fa-solid fa-play">
            Start
          </LinkButton>
        )}
        <RatingAndShareContainer>
          {userRole && setItemRating && (
            <RatingAlignRow>
              <RatingLabel>Rate this {selectedItem.category || selectedItem.type}:</RatingLabel>
              <Rating initialRating={challengeRating} fractions={1} onChange={updateMyRating} />
            </RatingAlignRow>
          )}
          <ShareOnTwitter url={window.location.href} title={selectedItem.textToShare} />
        </RatingAndShareContainer>
      </ButtonsContainer>
    );
  };

  return (
    <>
      <Helmet>
        <meta name="twitter:card" content="summary" />
        <meta name="title" content={selectedItem.name} />
        <meta property="og:title" content={selectedItem.name} />
        <meta property="twitter:title" content={selectedItem.name} />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta property="twitter:description" content={description} />
        <meta name="image" content={selectedItem.image} />
        <meta property="og:image" content={selectedItem.image} />
        <meta property="twitter:image" content={selectedItem.image} />
      </Helmet>
      <Container>
        <ContentContainer>
          <MetaInfoContainer>
            <Title>{selectedItem.name}</Title>
            {renderRatingNumberAndTags()}
            {isMobile && renderButtons()}
            <SectionContainer>
              <ChallengeLabel>Description</ChallengeLabel>
              <ChallengeDescription dangerouslySetInnerHTML={{ __html: description }} />
            </SectionContainer>
            <SectionContainer>
              <SectionColumn>
                {selectedItem.author && (
                  <SectionContainer>
                    <ChallengeLabel>Author</ChallengeLabel>
                    <AuthorContainer>
                      <AuthorAvatar
                        image={
                          selectedItem.author.avatar
                            ? selectedItem.author.avatar === '/images/placeholder-avatar.png'
                              ? Images.defaultAvatar
                              : selectedItem.author.avatar
                            : Images.defaultAvatar
                        }
                      />
                      <AuthorName href={`/profile?id=${selectedItem.author._id}`}>
                        {selectedItem.author.name}
                      </AuthorName>
                    </AuthorContainer>
                  </SectionContainer>
                )}
              </SectionColumn>
            </SectionContainer>
            {selectedItem.sponsor && selectedItem.landingPageLogo && (
              <SectionContainer center>
                <SponsorLogo image={selectedItem.landingPageLogo} />
              </SectionContainer>
            )}
          </MetaInfoContainer>
          <LinkAndImageContainer>
            {!isMobile && renderButtons()}
            <SelectedItemImage src={selectedItem.image} loading="lazy" alt={selectedItem.name} />
          </LinkAndImageContainer>
        </ContentContainer>
      </Container>
    </>
  );
}
